import { Icon } from '@iconify/react';
import { IconButton } from '@mui/material';
import MuiLink from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { alpha } from '@mui/material/styles';
import { useMemo, useState } from 'react';

import { CollapsibleCard, StyledGrid, StyledGridItem } from 'modules/shared/components';
import { PRIORITY_COLORS, PRIORITY_LABELS } from 'modules/shared/constants';
import { formatTimeString } from 'modules/shared/utils';
import { isTaskRunning, isTaskTerminal } from 'modules/work-implementation/projects-list/utils';

import { TaskName } from '../../shared/components';
import { getTaskSubmissions } from '../utils';
import TaskSubmissionForm from './TaskSubmissionForm';
import TaskSubmissionView from './TaskSubmissionView';

/**
 * Task view card with submission grid and revision handling.
 *
 * @param {Object} props
 * @param {Object} props.task
 * @param {Object} props.projectData
 * @param {Function} props.onTaskToggle
 * @param {Function} [props.onTaskSubmit]
 */
export default function TaskViewCard({ task, projectData, onTaskToggle, onTaskSubmit }) {
  const [isSubmissionOpen, setIsSubmissionOpen] = useState(false);

  const submissions = useMemo(() => getTaskSubmissions(task), [task]);

  const isRevisionRequested = Boolean(submissions[0]?.status === 'REVISION_REQUESTED');

  const showAddFile = submissions.length === 0 && !isSubmissionOpen;

  const showAddRevision = submissions.length === 1 && isRevisionRequested && !isSubmissionOpen;

  const handleSubmit = async (data) => {
    await onTaskSubmit?.(task, data);
    setIsSubmissionOpen(false);
  };

  return (
    <CollapsibleCard defaultExpanded borderVariant='outlined' colorVariant='default'>
      <div className='column gap-3'>
        {/* Header */}
        <div className='row gap-8'>
          <StyledGrid columns={3} sx={{ width: '100%' }}>
            <StyledGridItem columnIndex={0}>
              <TaskName taskName={task.name} />
            </StyledGridItem>
            <StyledGridItem columnIndex={1}>
              <span>Job code</span>
              <span>{projectData.code}</span>
            </StyledGridItem>
            <StyledGridItem columnIndex={2}>
              <span>Project name</span>
              <span>{projectData.name}</span>
            </StyledGridItem>
          </StyledGrid>

          {!isTaskTerminal(task.status) && (
            <div className='column gap-0'>
              <IconButton
                size='small'
                onClick={() => onTaskToggle(projectData.id, task)}
                sx={{ color: 'primary.light' }}
              >
                <Icon
                  icon={
                    isTaskRunning(task) ? 'material-symbols:pause' : 'material-symbols:play-arrow'
                  }
                />
              </IconButton>
              <span className='text-xs self-center'>{isTaskRunning(task) ? 'Pause' : 'Play'}</span>
            </div>
          )}
        </div>

        {/* Task meta */}
        <StyledGrid columns={3}>
          <StyledGridItem columnIndex={0}>
            <span>Work timeline</span>
            <span>{formatTimeString(task.totalTime)}</span>
          </StyledGridItem>

          <StyledGridItem columnIndex={1}>
            <span>Priority</span>
            <Typography
              component='span'
              sx={{
                color: PRIORITY_COLORS[task.priority],
                fontWeight: 600,
              }}
            >
              {PRIORITY_LABELS[task.priority]}
            </Typography>
          </StyledGridItem>

          <StyledGridItem columnIndex={2}>
            <span>Inward documents</span>
            <div className='column gap-1'>
              {task.documentsShared?.length === 0 && (
                <span className='text-sm'>No inward documents shared</span>
              )}
              {task.documentsShared?.map((doc) => (
                <MuiLink
                  key={doc.id}
                  href={doc.url}
                  target='_blank'
                  sx={{
                    textDecoration: 'underline',
                    color: (theme) => alpha(theme.palette.primary.light, 0.5),
                  }}
                >
                  {doc.name}
                </MuiLink>
              ))}
            </div>
          </StyledGridItem>
        </StyledGrid>

        {/* Note */}
        <StyledGrid columns={1}>
          <StyledGridItem columnIndex={0}>
            <span>Note / Comment</span>
            <span>{task.note || '-'}</span>
          </StyledGridItem>
        </StyledGrid>

        {/* Submissions */}
        {submissions.length > 0 && (
          <div className='submission-grid'>
            {submissions.length === 1 && (
              <div className='submission-row'>
                <TaskSubmissionView
                  submission={submissions[0]}
                  version={0}
                  canAddRevision={showAddRevision}
                  onAddRevision={() => setIsSubmissionOpen(true)}
                />

                {isSubmissionOpen && (
                  <TaskSubmissionForm
                    version={1}
                    onSubmit={handleSubmit}
                    onCancel={() => setIsSubmissionOpen(false)}
                  />
                )}
              </div>
            )}

            {submissions.length === 2 && (
              <div className='submission-row'>
                <TaskSubmissionView submission={submissions[0]} version={0} />
                <TaskSubmissionView submission={submissions[1]} version={1} />
              </div>
            )}

            {submissions.length > 2 && (
              <>
                <div className='submission-row'>
                  <TaskSubmissionView submission={submissions[0]} version={0} />
                  <TaskSubmissionView submission={submissions[1]} version={1} />
                </div>

                {submissions.slice(2).map((submission, index) => (
                  <div key={submission.id || index} className='submission-row'>
                    <TaskSubmissionView submission={submission} version={index + 2} />
                  </div>
                ))}
              </>
            )}
          </div>
        )}

        {/* r0 form */}
        {showAddFile && (
          <button
            type='button'
            onClick={() => setIsSubmissionOpen(true)}
            className='btn cta-outlined-btn self-end'
          >
            Add file
          </button>
        )}

        {isSubmissionOpen && submissions.length === 0 && (
          <TaskSubmissionForm
            version={0}
            onSubmit={handleSubmit}
            onCancel={() => setIsSubmissionOpen(false)}
          />
        )}
      </div>
    </CollapsibleCard>
  );
}
